<template>
  <div style="padding-top: 16px" id="page-container">
    <div class="page-section jobs_container">
      <div :class="containerClass">
        <div class="d-flex flex-column flex-sm-row align-items-sm-center mb-24pt" style="white-space: nowrap">
          <h2 class="mb-0 d-sm-block" v-if="!isMobSmallScreen">
            Research
            <i
              class="material-icons icon-16pt clickable-item"
              v-b-popover.hover.top="`Browse research opportunities available on Transition.`"
              >info</i
            >
          </h2>
          <div class="w-auto ml-sm-auto table d-flex align-items-center mb-sm-0">
            <small class="text-muted text-headings text-uppercase d-sm-block" v-if="!isMobSmallScreen">Sort by</small>
            <b-dropdown variant="link text-black-70" right no-caret>
              <template #button-content>
                <span>{{ orderingLabel }}<i class="fas fa-sort ml-1"></i></span>
              </template>
              <b-dropdown-item :active="ordering === '-research_type,-created_at'" @click="onSortChange('-research_type,-created_at')"
                >Most Relevant</b-dropdown-item
              >
              <b-dropdown-item :active="ordering === '-id'" @click="onSortChange('-id')">Most Recent</b-dropdown-item>
              <b-dropdown-item :active="ordering === 'title'" @click="onSortChange('title')">Title</b-dropdown-item>
              <b-dropdown-item :active="ordering === 'city'" @click="onSortChange('city')">Location</b-dropdown-item>
            </b-dropdown>
            <form class="search-form search-form--light d-lg-inline-flex mb-8pt mb-lg-0" @submit.prevent="">
              <b-form-input class="w-lg-auto" placeholder="Search..." v-model="searchTerm" @input="onSearch" />
              <b-btn variant="flush" type="submit">
                <md-icon v-text="'search'" />
              </b-btn>
            </form>
          </div>
        </div>

        <page-separator
          :title="
            !isLoading ? `showing ${totalResearch ? `${pageFromCount}-${pageToCount}` : 0} of ${totalResearch} Research` : ''
          "
        />

        <div class="d-flex justify-content-center" v-if="!totalResearch && !isLoading">
          <div class="card card-block card-stretch card-height col-md-8">
            <div class="card-body text-center">
              <i class="fas fa-exclamation-circle fa-lg text-muted mt-4 mb-4" style="font-size: 3rem" />
              <h4>No Research Found</h4>
              <p class="text-muted">
                Couldn't find any open research opportunities right now. Please check again later.
              </p>
            </div>
          </div>
        </div>

        <b-skeleton-wrapper :loading="isLoading" v-else>
          <template #loading>
            <div class="row card-group-row mb-lg-12pt home-card">
              <div class="col-sm-12 card-group-row__col" v-for="item in [1, 2]" :key="item">
                <div class="card overlay--show card-lg overlay--primary-dodger-blue card-group-row__card">
                  <div class="card-header d-flex align-items-center">
                    <a href="#" @click.prevent class="card-title flex"><b-skeleton width="100%"></b-skeleton></a>
                  </div>
                  <div class="card-body d-flex flex-column">
                    <b-skeleton width="100%"></b-skeleton>
                    <b-skeleton width="55%"></b-skeleton>
                    <b-skeleton width="70%"></b-skeleton>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <div class="row card-group-row mb-lg-12pt home-card">
            <research-card :research="research" />
          </div>
        </b-skeleton-wrapper>

        <pagination
          v-if="totalResearch > research.length"
          v-model="currentPage"
          :total-rows="totalResearch"
          :per-page="perPage"
          @change="onPageChange"
          aria-controls="research-table"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { debounce, get } from 'lodash';
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import Page from '@/components/Page.vue';
import Pagination from '../../../components/Ui/Pagination.vue';
import { mapActions } from 'vuex';
import ResearchCard from '../../Research/ResearchCard.vue';

export default {
  components: { Pagination, ResearchCard, PageSeparator },
  extends: Page,

  data() {
    return {
      title: 'Research',
      isLoading: true,
      perPage: 14,
      currentPage: 1,
      totalResearch: 0,
      research: [],
      searchTerm: '',
      ordering: '-research_type,-created_at',
      pageFromCount: 0,
      pageToCount: 0,
      windowWidth: window.innerWidth,
    };
  },

  computed: {
    isMobSmallScreen() {
      return this.windowWidth <= 420;
    },
    orderingLabel() {
      const labels = {
        '-research_type,-created_at': 'Most Relevant',
        '-id': 'Most Recent',
        title: 'Title',
        city: 'Location',
      };
      return labels[this.ordering] || 'Most Relevant';
    },
  },

  methods: {
    ...mapActions('research', ['getAllResearch']),
    get,

    async fetchResearch(pageNum = 1, params = {}) {
      this.isLoading = true;
      if (document.getElementById('page-container')) {
        document.getElementById('page-container').scrollIntoView();
      }
      const response = await this.getAllResearch({
        limit: this.perPage,
        offset: (pageNum - 1) * this.perPage,
        ...(this.ordering && { ordering: this.ordering }),
        ...(this.searchTerm && { search: this.searchTerm }),
        is_active: true,
        ...params,
      });

      this.research = response.data.results;
      this.currentPage = pageNum;
      this.totalResearch = response.data.count;
      this.pageFromCount = (this.currentPage - 1) * this.perPage + 1;
      this.pageToCount = this.pageFromCount + get(response.data, 'results.length', 0) - 1;
      this.isLoading = false;
    },

    onPageChange(pageNum) {
      this.fetchResearch(pageNum);
    },

    onSortChange(sortBy) {
      this.ordering = sortBy;
      this.fetchResearch();
    },

    onSearch() {
      this.debouncedSearch(this);
    },

    debouncedSearch: debounce((vm) => {
      vm.fetchResearch();
    }, 500),

    handleResize() {
      this.windowWidth = window.innerWidth;
    },
  },

  async mounted() {
    window.addEventListener('resize', this.handleResize);
    this.fetchResearch();
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
};
</script>
